import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { PlayCircle, QrCode } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/sonic`;

const SonicArtifacts = () => {
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    fetchArtifacts();
  }, []);

  const fetchArtifacts = async () => {
    try {
      const response = await axios.get(`${API}/artifacts`);
      setArtifacts(response.data);
    } catch (error) {
      console.error('Error fetching artifacts:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal">
      {/* Hero Section */}
      <div className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-burgundy/10 to-transparent" />
        <div className="max-w-6xl mx-auto relative z-10">
          <h1 
            className="text-6xl md:text-8xl font-black text-copper mb-6 tracking-tight"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            SONIC ARTIFACTS
          </h1>
          <p 
            className="text-xl md:text-2xl text-off-white/80 max-w-3xl leading-relaxed"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            An immersive gallery of voice recordings exploring memory, identity, and oral history.
            Scan QR codes to experience each artifact.
          </p>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div 
              className="text-copper text-2xl animate-pulse"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              Loading artifacts...
            </div>
          </div>
        ) : artifacts.length === 0 ? (
          <div className="text-center py-20">
            <p 
              className="text-off-white/60 text-xl mb-8"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              No artifacts yet. Visit the admin panel to upload recordings.
            </p>
            <Link
              to="/sonic/admin"
              className="inline-block px-8 py-4 bg-copper hover:bg-copper-dark text-charcoal font-bold rounded-lg transition-all duration-300 transform hover:scale-105"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
            >
              UPLOAD ARTIFACTS
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {artifacts.map((artifact) => (
              <Link
                key={artifact.id}
                to={`/sonic/play/${artifact.id}`}
                className="group relative"
                onMouseEnter={() => setHoveredId(artifact.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <div className="bg-charcoal border-2 border-copper/30 rounded-lg p-6 hover:border-copper transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl hover:shadow-copper/20">
                  {/* Animated Waveform Effect */}
                  <div className="mb-6 h-24 flex items-center justify-center gap-1">
                    {[...Array(20)].map((_, i) => (
                      <div
                        key={i}
                        className="w-1 bg-copper rounded-full transition-all duration-300"
                        style={{
                          height: hoveredId === artifact.id 
                            ? `${Math.random() * 100}%` 
                            : '30%',
                          opacity: hoveredId === artifact.id ? 1 : 0.5
                        }}
                      />
                    ))}
                  </div>

                  {/* Content */}
                  <div className="space-y-3">
                    <h3 
                      className="text-2xl font-bold text-off-white group-hover:text-copper transition-colors"
                      style={{ fontFamily: 'Unbounded, sans-serif' }}
                    >
                      {artifact.title}
                    </h3>
                    {artifact.description && (
                      <p 
                        className="text-off-white/70 text-sm line-clamp-2"
                        style={{ fontFamily: 'Rajdhani, sans-serif' }}
                      >
                        {artifact.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-4">
                      <div className="flex items-center gap-2 text-copper">
                        <PlayCircle className="h-5 w-5" />
                        <span 
                          className="text-sm font-semibold"
                          style={{ fontFamily: 'Rajdhani, sans-serif' }}
                        >
                          PLAY
                        </span>
                      </div>
                      <QrCode className="h-5 w-5 text-copper/60" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      <div className="max-w-7xl mx-auto px-6 py-12 flex justify-center gap-6">
        <Link
          to="/sonic/scanner"
          className="px-8 py-4 bg-burgundy hover:bg-burgundy/80 text-off-white font-bold rounded-lg transition-all duration-300 flex items-center gap-2"
          style={{ fontFamily: 'Unbounded, sans-serif' }}
        >
          <QrCode className="h-5 w-5" />
          SCAN QR CODE
        </Link>
        <Link
          to="/sonic/admin"
          className="px-8 py-4 border-2 border-copper text-copper hover:bg-copper hover:text-charcoal font-bold rounded-lg transition-all duration-300"
          style={{ fontFamily: 'Unbounded, sans-serif' }}
        >
          ADMIN PANEL
        </Link>
      </div>
    </div>
  );
};

export default SonicArtifacts;